<template>
  <transition v-if="showStatus || $slots.default" name="fade">
    <div
      class="auto-update-status"
      v-if="showStatus"
      role="status"
      aria-live="polite"
      v-bind="$attrs"
    >
      <div class="flex items-center justify-center gap-x-2">
        <FontAwesomeIcon
          v-if="errorEncountered"
          class="text-error"
          :icon="['fas', 'circle-xmark']"
          size="lg"
        />
        <LoadingSpinner v-else-if="isUpdating" class="size-4" />
        <FontAwesomeIcon
          v-else-if="hasUnsavedChanges"
          class="text-warning"
          :icon="['fas', 'circle-exclamation']"
          size="lg"
        />
        <FontAwesomeIcon v-else class="text-success" :icon="['fas', 'circle-check']" size="lg" />

        <span
          v-if="showLabel"
          class="text-sm font-medium whitespace-nowrap select-none"
          :class="statusLabelClass"
        >
          {{ statusLabel }}
        </span>
      </div>
    </div>
    <div v-else-if="$slots.default">
      <slot />
    </div>
  </transition>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue"
import LoadingSpinner from "@/components/loading/LoadingSpinner.vue"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"

const props = withDefaults(
  defineProps<{
    isUpdating: boolean
    errorEncountered: boolean
    hasUnsavedChanges?: boolean
    showLabel?: boolean
    /**
     * Time in milliseconds to hide the status after a successful save. Will be ignored if value < 0.
     */
    timeHideAfterSuccess?: number
  }>(),
  {
    hasUnsavedChanges: false,
    showLabel: false,
    timeHideAfterSuccess: -1
  }
)

defineOptions({
  inheritAttrs: false
})

const hide = ref(true)

const statusLabel = computed(() => {
  if (props.errorEncountered) return "Opslaan mislukt"
  if (props.isUpdating) return "Opslaan..."
  if (props.hasUnsavedChanges) return "Nog niet opgeslagen"
  return "Opgeslagen"
})

const statusLabelClass = computed(() => [
  props.errorEncountered
    ? "text-error"
    : props.isUpdating
      ? "text-gray-lighten-1"
      : props.hasUnsavedChanges
        ? "text-warning"
        : "text-success"
])

const showStatus = computed(() => {
  return (
    props.errorEncountered ||
    props.isUpdating ||
    props.hasUnsavedChanges ||
    !hide.value ||
    (props.showLabel && props.timeHideAfterSuccess < 0)
  )
})

let timeout: ReturnType<typeof setTimeout> | null = null
watch(
  () => props.isUpdating,
  (val) => {
    hide.value = false

    if (!val) {
      // After a successful save...
      if (props.timeHideAfterSuccess < 0) {
        // Do not hide.
        return
      }

      // Else, hide after a certain time.
      if (timeout) {
        clearTimeout(timeout)
        timeout = null
      }
      timeout = setTimeout(() => {
        hide.value = true
      }, props.timeHideAfterSuccess)
    }
  }
)
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition:
    opacity 0.5s,
    height 0.5s ease-in-out;
  overflow: hidden;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  height: 0;
}

.fade-enter-to,
.fade-leave-from {
  opacity: 1;
  height: 1.25rem;
}
</style>
